<template>
  <div class="cookie-consent">
    <section
      v-if="showBanner"
      class="cookie-banner"
      aria-labelledby="cookie-banner-title"
      aria-live="polite"
    >
      <img
        class="cookie-banner__logo"
        src="/img/SLMath/SLMath_Primary_Lockup_SVG.svg"
        alt="Simons Laufer Mathematical Sciences Institute"
      >
      <div class="cookie-banner__copy">
        <p class="cookie-banner__eyebrow">Privacy at SLMath</p>
        <h2 id="cookie-banner-title">Your privacy choices</h2>
        <p>
          We use essential cookies and similar technologies to keep this website secure, support
          features such as sign-in, and remember your privacy choices. With your permission, we
          also use optional analytics and third-party content to understand visits and improve the site.
        </p>
        <p class="cookie-banner__assurance">
          The site remains available if you choose Reject. You can change your choice at any time.
        </p>
        <div class="cookie-banner__links">
          <button type="button" class="cookie-link" @click="openSettings">Cookie details</button>
          <a href="/privacy-policy">Privacy Policy</a>
        </div>
      </div>
      <div class="cookie-banner__actions" aria-label="Cookie choices">
        <button type="button" class="cookie-button cookie-button--decision" @click="rejectOptional">
          Reject
        </button>
        <button type="button" class="cookie-button cookie-button--settings" @click="openSettings">
          Manage choices
        </button>
        <button type="button" class="cookie-button cookie-button--decision" @click="acceptOptional">
          Accept
        </button>
      </div>
    </section>

    <div v-if="settingsOpen" class="cookie-dialog-backdrop" @click.self="closeSettings">
      <section
        ref="dialog"
        class="cookie-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cookie-dialog-title"
        @keydown="handleDialogKeydown"
      >
        <header class="cookie-dialog__header">
          <div>
            <p class="cookie-banner__eyebrow">SLMath Cookie Settings</p>
            <h2 id="cookie-dialog-title">Choose what you share</h2>
          </div>
          <button ref="closeButton" type="button" class="cookie-dialog__close" aria-label="Close cookie settings" @click="closeSettings">
            <span aria-hidden="true">×</span>
          </button>
        </header>

        <p class="cookie-dialog__intro">
          Optional technologies are off until you allow them. Essential storage is always active because it is
          required for security, sign-in, navigation, and remembering this choice.
        </p>

        <div class="cookie-category">
          <div>
            <h3>Strictly necessary</h3>
            <p>Supports core site operation, account sessions, security, and privacy preferences.</p>
          </div>
          <span class="cookie-category__status">Always active</span>
        </div>

        <label class="cookie-category cookie-category--selectable">
          <div>
            <h3>Analytics</h3>
            <p>
              Google Analytics helps us understand page visits and site performance. We do not load it
              unless you enable this category.
            </p>
          </div>
          <span class="cookie-switch">
            <input v-model="draftPreferences.analytics" type="checkbox">
            <span aria-hidden="true" class="cookie-switch__track" />
            <span class="sr-only">Allow analytics cookies</span>
          </span>
        </label>

        <label class="cookie-category cookie-category--selectable">
          <div>
            <h3>Third-party content</h3>
            <p>
              Allows embedded video, maps, and similar features supplied by third parties. Those providers
              may receive information about your visit.
            </p>
          </div>
          <span class="cookie-switch">
            <input v-model="draftPreferences.embeddedMedia" type="checkbox">
            <span aria-hidden="true" class="cookie-switch__track" />
            <span class="sr-only">Allow third-party content</span>
          </span>
        </label>

        <footer class="cookie-dialog__footer">
          <a href="/privacy-policy">Read our Privacy Policy</a>
          <div class="cookie-dialog__footer-actions">
            <button type="button" class="cookie-button cookie-button--settings" @click="rejectOptional">
              Reject
            </button>
            <button type="button" class="cookie-button cookie-button--decision" @click="saveChoices">
              Save choices
            </button>
          </div>
        </footer>
      </section>
    </div>
  </div>
</template>

<script>
import {
	DEFAULT_COOKIE_PREFERENCES,
	readCookieConsent,
	saveCookieConsent,
} from "@/utils/cookieConsent"

export default {
	name: "CookieConsent",
	data() {
		return {
			showBanner: false,
			settingsOpen: false,
			previouslyFocusedElement: null,
			draftPreferences: { ...DEFAULT_COOKIE_PREFERENCES },
		}
	},
	mounted() {
		const consent = readCookieConsent()
		this.showBanner = !consent
		if (consent) this.draftPreferences = { ...consent.preferences }
		this.$root.$on("openCookieSettings", this.openSettings)
	},
	beforeDestroy() {
		this.$root.$off("openCookieSettings", this.openSettings)
		document.body.classList.remove("cookie-dialog-open")
	},
	methods: {
		applyConsent(preferences) {
			const consent = saveCookieConsent(preferences)
			this.draftPreferences = { ...consent.preferences }
			this.showBanner = false
			this.closeSettings()
		},
		acceptOptional() {
			this.applyConsent({ analytics: true, embeddedMedia: true })
		},
		rejectOptional() {
			this.applyConsent({ analytics: false, embeddedMedia: false })
		},
		saveChoices() {
			this.applyConsent(this.draftPreferences)
		},
		openSettings() {
			const consent = readCookieConsent()
			this.draftPreferences = consent ? { ...consent.preferences } : { ...DEFAULT_COOKIE_PREFERENCES }
			this.previouslyFocusedElement = document.activeElement
			this.settingsOpen = true
			document.body.classList.add("cookie-dialog-open")
			this.$nextTick(() => this.$refs.closeButton?.focus())
		},
		closeSettings() {
			if (!this.settingsOpen) return
			this.settingsOpen = false
			document.body.classList.remove("cookie-dialog-open")
			this.$nextTick(() => this.previouslyFocusedElement?.focus())
		},
		handleDialogKeydown(event) {
			if (event.key === "Escape") {
				event.preventDefault()
				this.closeSettings()
				return
			}
			if (event.key !== "Tab") return

			const focusable = Array.from(
				this.$refs.dialog.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'),
			)
			if (!focusable.length) return
			const first = focusable[0]
			const last = focusable[focusable.length - 1]
			if (event.shiftKey && document.activeElement === first) {
				event.preventDefault()
				last.focus()
			} else if (!event.shiftKey && document.activeElement === last) {
				event.preventDefault()
				first.focus()
			}
		},
	},
}
</script>

<style lang="scss">
body.cookie-dialog-open {
	overflow: hidden;
}

.cookie-consent {
	font-family: "Repro Regular", Arial, sans-serif;
}

.cookie-banner {
	position: fixed;
	z-index: 1090;
	right: 1.25rem;
	bottom: 1.25rem;
	left: 1.25rem;
	display: grid;
	grid-template-columns: minmax(11rem, 13rem) minmax(0, 1fr) minmax(13rem, 18rem);
	gap: 1.25rem;
	align-items: start;
	max-width: 76rem;
	margin: 0 auto;
	padding: 1.5rem;
	color: #21201f;
	background: #fcfaf8;
	border: 1px solid #e2d7d0;
	border-radius: 0.5rem;
	box-shadow: 0 1.25rem 4rem rgba(27, 18, 31, 0.24);
}

.cookie-banner__logo {
	display: block;
	width: 100%;
	max-width: 13rem;
	height: auto;
	margin-top: 0.15rem;
}

.cookie-banner__copy h2,
.cookie-dialog h2 {
	margin: 0 0 0.6rem;
	font-family: "Lyon Display Medium", Georgia, serif;
	font-size: 1.75rem;
	line-height: 1.15;
}

.cookie-banner__copy p,
.cookie-dialog p {
	margin-bottom: 0.55rem;
	line-height: 1.5;
}

.cookie-banner__eyebrow {
	margin: 0 0 0.35rem !important;
	color: #8b00bc;
	font-family: "Repro Bold", Arial, sans-serif;
	font-size: 0.75rem;
	letter-spacing: 0.08em;
	text-transform: uppercase;
}

.cookie-banner__assurance {
	font-family: "Repro Medium", Arial, sans-serif;
}

.cookie-banner__links {
	display: flex;
	gap: 1rem;
	align-items: center;
	margin-top: 0.75rem;
}

.cookie-banner__links a,
.cookie-dialog a,
.cookie-link {
	padding: 0;
	color: #5b2a78;
	font: inherit;
	font-family: "Repro Medium", Arial, sans-serif;
	text-decoration: underline;
	background: transparent;
	border: 0;
	cursor: pointer;
}

.cookie-banner__actions {
	display: grid;
	gap: 0.65rem;
}

.cookie-button {
	min-height: 2.75rem;
	padding: 0.65rem 1rem;
	font-family: "Repro Bold", Arial, sans-serif;
	border: 2px solid #5b2a78;
	border-radius: 0.2rem;
	cursor: pointer;
	transition: transform 140ms ease, box-shadow 140ms ease, background-color 140ms ease;
}

.cookie-button:hover {
	transform: translateY(-1px);
	box-shadow: 0 0.3rem 0.8rem rgba(38, 19, 49, 0.16);
}

.cookie-button:focus-visible,
.cookie-link:focus-visible,
.cookie-dialog__close:focus-visible,
.cookie-banner__links a:focus-visible,
.cookie-dialog a:focus-visible {
	outline: 3px solid #f5b335;
	outline-offset: 3px;
}

.cookie-button--decision {
	color: #fff;
	background: #5b2a78;
}

.cookie-button--decision:hover {
	background: #46205e;
}

.cookie-button--settings {
	color: #5b2a78;
	background: #fff;
}

.cookie-dialog-backdrop {
	position: fixed;
	z-index: 1100;
	inset: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 1rem;
	background: rgba(22, 16, 26, 0.62);
}

.cookie-dialog {
	width: 100%;
	max-width: 47rem;
	max-height: calc(100vh - 2rem);
	overflow-y: auto;
	padding: 1.75rem;
	color: #21201f;
	background: #fcfaf8;
	border-radius: 0.5rem;
	box-shadow: 0 1.5rem 5rem rgba(0, 0, 0, 0.35);
}

.cookie-dialog__header,
.cookie-dialog__footer,
.cookie-category {
	display: flex;
	gap: 1rem;
	align-items: center;
	justify-content: space-between;
}

.cookie-dialog__close {
	display: flex;
	flex: 0 0 auto;
	align-items: center;
	justify-content: center;
	width: 2.5rem;
	height: 2.5rem;
	color: #21201f;
	font-size: 2rem;
	line-height: 1;
	background: #fff;
	border: 1px solid #c8bbb4;
	border-radius: 50%;
	cursor: pointer;
}

.cookie-dialog__intro {
	padding-bottom: 1rem;
	border-bottom: 1px solid #e2d7d0;
}

.cookie-category {
	min-height: 7rem;
	padding: 1rem 0;
	border-bottom: 1px solid #e2d7d0;
}

.cookie-category h3 {
	margin: 0 0 0.35rem;
	font-family: "Repro Bold", Arial, sans-serif;
	font-size: 1rem;
}

.cookie-category p {
	max-width: 35rem;
	margin: 0;
	color: #4e5155;
}

.cookie-category__status {
	flex: 0 0 auto;
	padding: 0.3rem 0.65rem;
	color: #3b2149;
	font-family: "Repro Bold", Arial, sans-serif;
	font-size: 0.75rem;
	background: #efe6f4;
	border-radius: 2rem;
}

.cookie-category--selectable {
	cursor: pointer;
}

.cookie-switch {
	position: relative;
	flex: 0 0 auto;
	width: 3.25rem;
	height: 1.8rem;
}

.cookie-switch input {
	position: absolute;
	z-index: 2;
	inset: 0;
	width: 100%;
	height: 100%;
	margin: 0;
	opacity: 0;
	cursor: pointer;
}

.cookie-switch__track {
	position: absolute;
	inset: 0;
	background: #726e73;
	border-radius: 2rem;
	transition: background-color 160ms ease;
}

.cookie-switch__track::after {
	position: absolute;
	top: 0.2rem;
	left: 0.2rem;
	width: 1.4rem;
	height: 1.4rem;
	content: "";
	background: #fff;
	border-radius: 50%;
	box-shadow: 0 1px 4px rgba(0, 0, 0, 0.25);
	transition: transform 160ms ease;
}

.cookie-switch input:checked + .cookie-switch__track {
	background: #5b2a78;
}

.cookie-switch input:checked + .cookie-switch__track::after {
	transform: translateX(1.45rem);
}

.cookie-switch input:focus-visible + .cookie-switch__track {
	outline: 3px solid #f5b335;
	outline-offset: 3px;
}

.cookie-dialog__footer {
	align-items: flex-end;
	padding-top: 1.25rem;
}

.cookie-dialog__footer-actions {
	display: flex;
	gap: 0.65rem;
}

@media (max-width: 800px) {
	.cookie-banner {
		grid-template-columns: minmax(9.5rem, 11rem) 1fr;
		gap: 0.85rem;
		padding: 1rem;
	}

	.cookie-banner__logo {
		max-width: 11rem;
	}

	.cookie-banner__actions {
		grid-column: 1 / -1;
		grid-template-columns: 1fr 1fr 1fr;
	}
}

@media (max-width: 560px) {
	.cookie-banner {
		right: 0.6rem;
		bottom: 0.6rem;
		left: 0.6rem;
		grid-template-columns: 1fr;
		max-height: calc(100vh - 1.2rem);
		overflow-y: auto;
	}

	.cookie-banner__logo {
		max-width: 10rem;
	}

	.cookie-banner__actions,
	.cookie-dialog__footer-actions {
		display: grid;
		grid-template-columns: 1fr;
		width: 100%;
	}

	.cookie-dialog {
		padding: 1.1rem;
	}

	.cookie-category,
	.cookie-dialog__footer {
		align-items: flex-start;
		flex-direction: column;
	}

	.cookie-dialog__footer-actions .cookie-button {
		width: 100%;
	}
}

@media (prefers-reduced-motion: reduce) {
	.cookie-button,
	.cookie-switch__track,
	.cookie-switch__track::after {
		transition: none;
	}
}
</style>
